import React, { useEffect, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { accountByActiveByNameDropDown } from "../../graphql/account/account-queries";

import { CircularProgress, InputAdornment } from "@material-ui/core";

import { AutocompleteInput, useInput, useNotify } from "react-admin";

import { logError } from "../../utils/appUtils";
import { accountNameRenderer } from "./AccountNameRenderer";

const getAccounts = async (
  setAccounts,
  notify,
  setFetching,
  skipInvoice,
  excludeAccountIds = [],
  onAccountsLoaded,
  filterAccounts,
) => {
  try {
    const response = await API.graphql(
      graphqlOperation(accountByActiveByNameDropDown, {
        reloadYardId: window.appUser?.reloadYardId,
        activeBusinessName: { beginsWith: { active: "true" } },
        limit: 1000,
      }),
    );
    let accounts = response.data.accountByActiveByName.items;
    if (skipInvoice) {
      accounts = accounts.filter((itm) => !itm.skipInvoice);
    }
    if (excludeAccountIds.length > 0) {
      accounts = accounts.filter((itm) => !excludeAccountIds.includes(itm.id));
    }
    if (filterAccounts) {
      accounts = await filterAccounts(accounts, notify);
    }
    setAccounts(accounts);
    if (onAccountsLoaded) {
      onAccountsLoaded(accounts);
    }
  } catch (err) {
    logError("common:AccountDropDown:getAccounts", err, notify);
  } finally {
    // A failed load or filter must not leave the picker spinning.
    setFetching(false);
  }
};

export const AccountDropDown = ({
  source,
  label,
  onAccountChange,
  helperText,
  validate,
  allowEmpty = true,
  alwaysOn = true,
  loading = false, // external control
  skipInvoice = false, // filter out non-invoiceable accounts
  excludeAccountIds = [], // hide specific accounts by id
  alertOverDue = false, // alert over due / over limit accounts
  onAccountsLoaded,
  filterAccounts, // async (accounts, notify) => accounts; keep it referentially stable
  ...props
}) => {
  const notify = useNotify();
  const [accounts, setAccounts] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [overDue, setOverDue] = useState(false);
  const [overLimit, setOverLimit] = useState(false);
  const isLoading = loading || fetching;
  const excludeAccountIdsKey = excludeAccountIds.join("|");

  const { input, meta } = useInput({ source, validate, ...props });
  const hasError = (meta.touched && meta?.error) || meta?.submitError;

  useEffect(() => {
    setFetching(true);
    getAccounts(
      setAccounts,
      notify,
      setFetching,
      skipInvoice,
      excludeAccountIdsKey ? excludeAccountIdsKey.split("|") : [],
      onAccountsLoaded,
      filterAccounts,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipInvoice, excludeAccountIdsKey, onAccountsLoaded, filterAccounts]);

  const handleAccountChange = (value) => {
    if (!onAccountChange) return;

    let selectedAccount = null;

    if (value) {
      selectedAccount = accounts.find((itm) => itm.id === value);
      setOverDue(selectedAccount?.overDue);
      setOverLimit(selectedAccount?.overLimit);
      if (alertOverDue && selectedAccount) {
        if (selectedAccount.overLimit) {
          notify("Account is over credit limit.", "error");
        } else if (selectedAccount.overDue) {
          notify("Account is over due.", "warning");
        }
      }
    } else {
      // Cleared selection; reset local flags
      setOverDue(false);
      setOverLimit(false);
    }

    onAccountChange(selectedAccount);
  };

  const getHelperText = () => {
    if (alertOverDue && overLimit) {
      return "⚠️ Account over credit limit.";
    } else if (alertOverDue && overDue) {
      return "⚠️ Account over due.";
    }
    return helperText || false;
  };

  return (
    <>
      <AutocompleteInput
        {...input}
        {...props}
        source={source}
        label={label}
        variant="outlined"
        fullWidth
        allowEmpty={allowEmpty}
        resettable={!isLoading} // disables reset button logic while loading
        clearable
        alwaysOn={alwaysOn}
        validate={validate}
        choices={accounts}
        optionText={accountNameRenderer}
        onChange={(value) => {
          handleAccountChange(value);
        }}
        loading={fetching}
        loaded={!fetching}
        helperText={getHelperText()}
        options={{
          error: (alertOverDue && (overDue || overLimit)) || hasError,
          InputProps: {
            endAdornment: (
              <InputAdornment position="end">
                {isLoading && <CircularProgress size={20} color="inherit" />}
              </InputAdornment>
            ),
          },
        }}
      />
    </>
  );
};
